// Client-side analytics helpers.
//
// `useTrack()` returns a fire-and-forget tracker bound to the current GraphQL
// client (web/mobile/admin all wire the same `gql` client via GqlProvider).
// The hook never throws — analytics must not break the host call site.

import { useCallback, useEffect, useRef } from 'react';
import { useGql } from '@ah/design';
import type {
  AnalyticsEvent,
  AnalyticsEventType,
  AnalyticsSurface,
} from '@ah/shared/analytics';

export const RECORD_EVENT_M = /* GraphQL */ `
  mutation RecordAnalyticsEvent($event: AnalyticsEventInput!) {
    recordEvent(event: $event)
  }
`;

export const ANALYTICS_EVENTS_Q = /* GraphQL */ `
  query AnalyticsEvents($limit: Int, $before: ID, $type: String, $userId: ID) {
    analyticsEvents(limit: $limit, before: $before, type: $type, userId: $userId) {
      id
      occurredAt
      type
      payload
      label
      href
      surface
      schemaVersion
      sessionId
      user {
        id
        name
        pictureUrl
      }
    }
  }
`;

export interface AnalyticsEventRow {
  id: string;
  occurredAt: string;
  type: string;
  payload: Record<string, unknown>;
  label: string;
  href: string | null;
  surface: string | null;
  schemaVersion: number;
  sessionId: string | null;
  user: { id: string; name: string | null; pictureUrl: string | null } | null;
}

const SESSION_KEY = 'fg.analytics.sid';

/** Stable per-tab session id stored in sessionStorage. */
function getOrCreateSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return null;
  }
}

export type Track = <T extends AnalyticsEventType>(
  type: T,
  payload: Extract<AnalyticsEvent, { type: T }>['payload'],
) => void;

export interface UseTrackOptions {
  surface: AnalyticsSurface;
}

/**
 * Returns a fire-and-forget tracker. Caller does not await — the mutation
 * runs in the background and any failure is swallowed.
 */
export function useTrack({ surface }: UseTrackOptions): Track {
  const gql = useGql();
  // The discriminated-union generic narrows correctly at call sites; inside
  // the implementation TS can't prove payload matches type, so we cast.
  const fn = useCallback(
    (type: string, payload: unknown) => {
      const event = { type, payload, sessionId: getOrCreateSessionId(), surface };
      gql
        .request(RECORD_EVENT_M, { event })
        .catch((err: unknown) => {
          if (typeof console !== 'undefined') console.debug('[track] failed', err);
        });
    },
    [gql, surface],
  );
  return fn as Track;
}

/**
 * Convenience: emit a `view_route` event whenever `path` changes. Pair with
 * a router hook (useLocation) at the app shell level.
 */
export function useTrackRoute(
  path: string,
  surface: AnalyticsSurface,
  title?: string,
): void {
  const track = useTrack({ surface });
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (last.current === path) return;
    last.current = path;
    track('view_route', { path, ...(title ? { title } : {}) });
  }, [path, title, track]);
}
